import { IWidgetControllerConfig } from '@wix/native-components-infra/dist/src/types/types';
import {
  ActionsFactory,
  SetState,
  StateChangeCallback,
  GetControllerState,
  ViewModelFactory,
  SetStateOptions,
} from './ControlledComponent.types';
import { debounce } from 'lodash';
import { BASE_DELAY } from '../../constants/constants';

export type Render<ControllerStateType> = ({
  initial,
  resetState,
  updatedState,
}: {
  initial?: boolean;
  resetState?: boolean;
  updatedState?: Partial<ControllerStateType>;
}) => Promise<void>;

export async function createControlledComponent<
  ControllerStateType,
  ControllerActionTypes,
  ControllerViewModelType,
  ContextType,
>({
  controllerConfig,
  initialState,
  viewModelFactory,
  actionsFactory,
  context,
  isNavigationLoaderEnabled,
}: {
  controllerConfig: IWidgetControllerConfig;
  initialState: ControllerStateType;
  viewModelFactory: ViewModelFactory<
    ControllerStateType,
    ControllerViewModelType,
    ContextType
  >;
  actionsFactory: ActionsFactory<
    ControllerStateType,
    ControllerActionTypes,
    ContextType
  >;
  context: ContextType;
  isNavigationLoaderEnabled?: boolean;
}): Promise<{
  onStateChange: (callback: StateChangeCallback<ControllerStateType>) => void;
  render: Render<ControllerStateType>;
  controllerActions: ControllerActionTypes;
  setState: Function;
}> {
  const { setProps } = controllerConfig;
  let state: ControllerStateType = initialState;
  const stateChangedListeners: StateChangeCallback<ControllerStateType>[] = [];

  const onStateChange = (
    callback: StateChangeCallback<ControllerStateType>,
  ) => {
    stateChangedListeners.push(callback);
  };

  const applyState = () => {
    stateChangedListeners.forEach((listener) => listener(state));
    render({});
  };

  const debouncedApplyState = debounce(() => {
    applyState();
  }, BASE_DELAY);

  const setState: SetState<ControllerStateType> = (
    stateToUpdate: Partial<ControllerStateType>,
    { immediate }: SetStateOptions = {},
  ) => {
    state = { ...state, ...stateToUpdate };

    if (isNavigationLoaderEnabled && immediate) {
      applyState();
    } else {
      debouncedApplyState();
    }
  };

  const getControllerState: GetControllerState<ControllerStateType> = () => [
    state,
    setState,
  ];

  const controllerActions = actionsFactory({
    getControllerState,
    context,
  });

  const render: Render<ControllerStateType> = async ({
    initial,
    resetState = false,
    updatedState = {},
  }) => {
    const latestState = resetState ? initialState : state;
    const viewModel = viewModelFactory({
      state: { ...latestState, ...updatedState },
      context,
    });
    setProps({
      ...viewModel,
      ...(initial
        ? {
            ...controllerActions,
            fitToContentHeight: true,
          }
        : {}),
    });
  };

  await render({ initial: true });

  return { onStateChange, render, controllerActions, setState };
}
